"use client";

import { Button, LoginHeroCarousel, LoginTestimonialSlide, type LoginTestimonial, Typography, CreatorTrustRow } from "@artha/ui";
import type { SocialProvider } from "@artha/types";
import { SocialAuthProvider } from "@artha/types";
import { useSocialLogin } from "@/modules/auth/hooks/useSocialLogin";
import {
    AUTH_PRIMARY_BUTTON_CLASS,
    AUTH_SECONDARY_BUTTON_CLASS,
    AuthResponsivePage,
} from "@/modules/auth";
import { useT } from "@/lib/useT";
import { cn } from "@/lib/utils";
import { useSearchParams } from "next/navigation";
import {
    GoogleIcon,
    FacebookIcon,
    LinkedInIcon,
} from "@/components/icons";
import { getStoredReferralAttribution } from "@/modules/referrals/services/referralStorage";

type SocialProviderOption = {
    id: string;
    name: string;
    icon: React.ReactNode;
    provider?: SocialProvider;
    onClick?: () => void;
};

type PrimaryProviderConfig = {
    provider: SocialProvider;
    name: string;
    icon: React.ReactNode;
};

export default function LoginContent() {
    const { auth } = useT();
    const { loginWithProvider, loadingProvider, error } = useSocialLogin();
    const searchParams = useSearchParams();
    const referrer = searchParams.get("ref");
    const referralToken =
        searchParams.get("referralToken") ||
        getStoredReferralAttribution()?.attributionToken ||
        undefined;
    const titleLeadingText = auth("loginTitleLeading");
    const titleAudienceText = auth("loginTitleAudience");
    const highlightText = auth("loginTitleHighlight");

    // Define primary provider configurations
    const primaryProviderConfigs: Record<string, PrimaryProviderConfig> = {
        [SocialAuthProvider.GOOGLE]: {
            provider: SocialAuthProvider.GOOGLE,
            name: "Google",
            icon: <GoogleIcon className="w-5 h-5" />,
        },
        [SocialAuthProvider.LINKEDIN]: {
            provider: SocialAuthProvider.LINKEDIN,
            name: "LinkedIn",
            icon: <LinkedInIcon className="text-button-primary w-5 h-5" />,
        },
        [SocialAuthProvider.FACEBOOK]: {
            provider: SocialAuthProvider.FACEBOOK,
            name: "Facebook",
            icon: <FacebookIcon className="text-[#1877F2] size-6" />,
        }
    };

    // Determine primary provider based on referrer, default to LinkedIn
    const primaryProvider = primaryProviderConfigs[referrer || SocialAuthProvider.LINKEDIN] || primaryProviderConfigs[SocialAuthProvider.LINKEDIN];

    // Define remaining social providers (excluding the primary one)
    const socialProviders: SocialProviderOption[] = [
        {
            id: SocialAuthProvider.GOOGLE,
            name: "Google",
            icon: <GoogleIcon />,
            provider: SocialAuthProvider.GOOGLE,
        },
        {
            id: SocialAuthProvider.LINKEDIN,
            name: "LinkedIn",
            icon: <LinkedInIcon className="text-button-primary w-5 h-5" />,
            provider: SocialAuthProvider.LINKEDIN,
        },
        {
            id: SocialAuthProvider.FACEBOOK,
            name: "Facebook",
            icon: <FacebookIcon className="text-[#1877F2] size-6" />,
            provider: SocialAuthProvider.FACEBOOK,
        }
    ].filter(provider => provider.provider !== primaryProvider.provider);

    const handleSocialClick = (option: SocialProviderOption) => {
        if (option.provider) {
            void loginWithProvider(option.provider, undefined, referralToken);
            return;
        }
        option.onClick?.();
    };

    const testimonials: LoginTestimonial[] = [
        {
            quote: "I was skeptical at first, but getting my first application within 10 minutes of posting on LinkedIn changed everything. With consistent team support, sharing jobs on artha.link is now part of my daily routine.",
            author: "Thomasina Hinds",
            handle: "@thomasina_hinds",
            role: "Senior Staffing Coordinator",
            followers: "14.1k followers",
            avatar: "/images/testimonial-thomasina.png"
        },
        {
            quote: "artha.link turned my link-in-bio into a job discovery tool, helping my audience find real jobs now I get 30+ genuine applications every week.",
            author: "Akshata N Bhat",
            handle: "@akshata-n-bhat",
            role: "Career Tips Creator",
            followers: "27.1k followers",
            avatar: "/images/testimonial-akshata.jpg"
        },
        {
            quote: "In just 2 months with artha.link, I've helped my Job Campus India community discover relevant jobs while earning from it. It's already become my highest-performing passive income source.",
            author: "Job Campus India",
            handle: "@jobs_campus_india298455",
            role: "YouTube Creator",
            followers: "8.4k subscribers",
            avatar: "/images/testimonial-jobscampus.jpg"
        }
    ];

    const heroSlides = testimonials.map((testimonial, index) => (
        <LoginTestimonialSlide
            key={`testimonial-${index}`}
            testimonial={testimonial}
        />
    ));

    return (
        <AuthResponsivePage
            hero={<LoginHeroCarousel className="w-full shrink-0 min-h-[320px] h-auto" slides={heroSlides} />}
        >
            <div className="mb-4">
                <Typography variant="h3" className="text-[24px] font-bold text-black tracking-tight leading-[1.4] mb-2">
                    <span className="font-bold">{titleLeadingText}</span>
                    <span className="inline-block bg-[#ED5AA9] text-white border border-black px-2 py-0.5 shadow-[2.5px_2.5px_0px_0px_#000000] rotate-[-2deg] font-bold ml-1.5 whitespace-nowrap text-[24px] tracking-normal">
                        {titleAudienceText}
                    </span>
                    <span className="inline-block ml-1.5">{highlightText}</span>
                </Typography>
                <Typography variant="p" className="text-[#475467] text-[15px] font-medium leading-normal">
                    {auth("loginSubtitle")}
                </Typography>
            </div>

            <div className="flex flex-col gap-3.5 w-full">
                <div className="grid grid-cols-2 items-center w-full gap-3">
                    {socialProviders.map((provider) => (
                        <Button
                            key={provider.id}
                            size="lg"
                            className={AUTH_SECONDARY_BUTTON_CLASS}
                            onClick={() => handleSocialClick(provider)}
                            aria-label={auth("signInWith", { provider: provider.name })}
                            disabled={
                                provider.provider
                                    ? loadingProvider === provider.provider
                                    : false
                            }
                        >
                            {provider.icon}
                            <span className="text-black text-sm font-medium">{provider.name}</span>
                        </Button>
                    ))}
                </div>

                <Button
                    size="lg"
                    showShine
                    className={cn(AUTH_PRIMARY_BUTTON_CLASS, "gap-2 bg-primary text-white")}
                    onClick={() => void loginWithProvider(primaryProvider.provider, undefined, referralToken)}
                    disabled={loadingProvider === primaryProvider.provider}
                >
                    <span className="mr-1 relative z-10">
                        {auth("continueWith", { provider: primaryProvider.name })}
                    </span>
                    <div className="bg-white rounded-[4px] p-0.5 flex items-center justify-center h-6 w-6 shrink-0 relative z-10">
                        {primaryProvider.icon}
                    </div>
                </Button>
            </div>

            <CreatorTrustRow className="mt-6" />

            {error && (
                <p className="text-sm text-red-600 mt-4" role="alert">
                    {error}
                </p>
            )}
        </AuthResponsivePage>
    );
}
